import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MarketingAttributionEvent } from "@redotech/redo-model/marketing";
import { RedoMetricItem } from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import {
  RedoTable,
  RedoTableColumn,
  RedoTableLayout,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTableBadgeCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import {
  ChannelFilter,
  getChannelsForFilter,
} from "../filters/marketing-channel-filter";

export const MarketingAnalyticsAutomationPerformance = memo(
  function MarketingAnalyticsAutomationPerformance({
    dateFilter,
    attributionDays,
    channelFilter,
    attributionEvents,
  }: {
    dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
    attributionDays: number;
    channelFilter: ChannelFilter;
    attributionEvents: MarketingAttributionEvent[];
  }) {
    const { formatCurrency } = useContext(CurrencyContext);

    const { startDate, endDate } = useMemo(() => {
      if (!dateFilter) {
        // Default to last 30 days if no valid date range
        const now = new Date();
        const thirtyDaysAgo = new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000,
        );
        return {
          startDate: Temporal.Instant.from(thirtyDaysAgo.toISOString()),
          endDate: Temporal.Instant.from(now.toISOString()),
        };
      }
      return {
        startDate: Temporal.Instant.from(
          new Date(dateFilter[0].toString()).toISOString(),
        ),
        endDate: Temporal.Instant.from(
          new Date(dateFilter[1].toString()).toISOString(),
        ),
      };
    }, [dateFilter]);

    const {
      data: automationPerformanceResult,
      isLoading,
      error: failedToLoad,
    } = useRpcQuery(RedoMarketingRpcClientContext, {
      method: "getAutomationPerformanceAnalytics",
      args: {
        startDate,
        endDate,
        attributionDays,
        channels: getChannelsForFilter(channelFilter),
        attributionEvents,
      },
    });

    const formatRoiMultiplier = (value: number) => {
      const multiplier = value / 100 + 1;
      return `${multiplier.toFixed(1)}×`;
    };

    const calculatePercentageChange = (current: number, previous: number) => {
      if (previous === 0 && current === 0) return 0;
      if (previous === 0) {
        return null;
      }

      const change = ((current - previous) / previous) * 100;

      // Cap extremely large percentage changes for display purposes
      if (change > 999) return 999;
      if (change < -999) return -999;

      return change;
    };

    const totals = automationPerformanceResult?.totals;
    const automations = useMemo(
      () => automationPerformanceResult?.automations || [],
      [automationPerformanceResult?.automations],
    );

    type AutomationRow = (typeof automations)[number];

    const tableColumns = useMemo((): RedoTableColumn<AutomationRow>[] => {
      return [
        {
          key: "name",
          widthMode: { type: "fill" },
          alignment: "left",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Automation" }),
          renderNormalCell: ({ row }) => {
            const sentDate = row.SENT_DATE
              ? row.SENT_DATE.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "Not sent";
            return (
              <RedoTableTextCell
                subtext={`Sent ${sentDate} • ${row.UNIQUE_RECIPIENTS.toLocaleString()} recipients`}
                text={row.NAME}
              />
            );
          },
        },
        {
          key: "channel",
          widthMode: { type: "fixed", width: 100 },
          alignment: "center",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Channel" }),
          renderNormalCell: ({ row }) => {
            const channelColor =
              row.CHANNEL === "email" ? "blueLight" : "purple";
            const channelText = row.CHANNEL === "email" ? "Email" : "SMS";
            return (
              <RedoTableBadgeCell
                badges={[{ text: channelText, color: channelColor }]}
              />
            );
          },
        },
        {
          key: "ctr",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "CTR" }),
          renderNormalCell: ({ row }) => {
            const ctr =
              row.TOTAL_SENDS > 0
                ? (row.TOTAL_CLICKS / row.TOTAL_SENDS) * 100
                : 0;
            return <RedoTableTextCell text={`${ctr.toFixed(1)}%`} />;
          },
        },
        {
          key: "cvr",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "CVR" }),
          renderNormalCell: ({ row }) => {
            const cvr =
              row.TOTAL_CLICKS > 0
                ? (row.TOTAL_ORDERS / row.TOTAL_CLICKS) * 100
                : 0;
            return <RedoTableTextCell text={`${cvr.toFixed(1)}%`} />;
          },
        },
        {
          key: "cost",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Spend" }),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={formatCurrency(row.SPEND)} />
          ),
        },
        {
          key: "roi",
          widthMode: { type: "fixed", width: 100 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "ROI" }),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={formatRoiMultiplier(row.ROI ?? 0)} />
          ),
        },
        {
          key: "revenue",
          widthMode: { type: "fixed", width: 120 },
          alignment: "right",
          renderHeaderCell: () =>
            StandardRedoTableHeaderCell({ headerText: "Attributed Revenue" }),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={formatCurrency(row.REVENUE)} />
          ),
        },
      ];
    }, [formatCurrency]);

    const tableLayout: RedoTableLayout<AutomationRow> = useMemo(
      () => ({ columns: tableColumns, headerColor: "white", standalone: true }),
      [tableColumns],
    );

    // Get top 5 automations by revenue (already sorted from backend)
    const topAutomations = useMemo(
      () => automations.slice(0, 5),
      [automations],
    );

    const revenueChange = totals
      ? calculatePercentageChange(
          totals.totalRevenue,
          totals.previousTotalRevenue,
        )
      : 0;
    const spendChange = totals
      ? calculatePercentageChange(totals.totalSpend, totals.previousTotalSpend)
      : 0;
    const roiChange = totals
      ? calculatePercentageChange(totals.totalROI, totals.previousTotalROI)
      : 0;
    const revenuePerRecipientChange = totals
      ? calculatePercentageChange(
          totals.totalRecipients > 0
            ? totals.totalRevenue / totals.totalRecipients
            : 0,
          totals.previousTotalRecipients > 0
            ? totals.previousTotalRevenue / totals.previousTotalRecipients
            : 0,
        )
      : 0;

    if (failedToLoad) {
      return (
        <Flex dir="column" gap="4xl" w="full">
          <Text fontSize="md" fontWeight="semibold">
            Automation Performance
          </Text>
          <Text color="red.500">Error loading automation performance data</Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="xl" w="full">
        <Text fontSize="md" fontWeight="semibold">
          Automation Performance
        </Text>
        <Flex dir="row" gap="4xl" w="full">
          <RedoMetricItem
            pending={isLoading}
            title="Automation Revenue"
            value={formatCurrency(totals?.totalRevenue ?? 0)}
            {...(revenueChange !== null && {
              comparisonChange: revenueChange,
              comparisonText: "vs previous period",
              isGoodChange: revenueChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Automation Spend"
            value={formatCurrency(totals?.totalSpend ?? 0)}
            {...(spendChange !== null && {
              comparisonChange: spendChange,
              comparisonText: "vs previous period",
              isGoodChange: spendChange <= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Automation ROI"
            value={formatRoiMultiplier(totals?.totalROI ?? 0)}
            {...(roiChange !== null && {
              comparisonChange: roiChange,
              comparisonText: "vs previous period",
              isGoodChange: roiChange >= 0,
            })}
          />
          <RedoMetricItem
            pending={isLoading}
            title="Revenue per recipient"
            value={
              totals?.totalRecipients && totals.totalRecipients > 0
                ? formatCurrency(totals.totalRevenue / totals.totalRecipients)
                : formatCurrency(0)
            }
            {...(revenuePerRecipientChange !== null && {
              comparisonChange: revenuePerRecipientChange,
              comparisonText: "vs previous period",
              isGoodChange: revenuePerRecipientChange >= 0,
            })}
          />
        </Flex>

        <Text fontSize="md" fontWeight="semibold">
          Top automations
        </Text>
        {/* Top Performing Automations Table */}
        <Flex dir="column" gap="xl" w="full">
          <RedoTable<AutomationRow>
            emptyStateContent={
              automations.length === 0 && !isLoading ? (
                <Flex align="center" justify="center" py="8xl">
                  <Text color="gray.500">
                    {getEmptyStateMessage(channelFilter, "automation")}
                  </Text>
                </Flex>
              ) : undefined
            }
            layout={tableLayout}
            loadingStateContent={
              isLoading ? (
                <Flex align="center" justify="center" py="8xl">
                  <LoadingRedoAnimation />
                </Flex>
              ) : undefined
            }
            rowsOfData={isLoading ? [] : topAutomations}
            size="md"
          />
        </Flex>
      </Flex>
    );
  },
);

export function getEmptyStateMessage(
  channelFilter: ChannelFilter,
  entityName: string,
): string {
  if (channelFilter === "all") {
    return `No ${entityName} data available`;
  }
  return `No ${channelFilter === "email" ? "email" : "SMS"} ${entityName} found`;
}
